/* Tarjeta de producto — al pasar el mouse (o llegar con el teclado) por una
   miniatura de variante, la foto grande de la tarjeta cambia a esa variante.

   El cambio se queda puesto a propósito: si al salir el mouse volviera a la
   foto original, comparar colores en una cuadrícula sería imposible.

   En un celular no hay hover, así que ahí el primer toque muestra la variante
   y el segundo entra al producto. Sin ese matiz, en móvil era imposible ver
   los colores desde el listado: el toque se iba derecho al enlace.

   Cada miniatura es un enlace normal a su variante, así que si el JavaScript
   no carga, sigue funcionando: solo se pierde la vista previa. */

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('.pcard__img');
    this.swatches = this.querySelectorAll('[data-swatch]');

    if (!this.image || !this.swatches.length) return;

    const hasPointerEvents = 'PointerEvent' in window;

    this.swatches.forEach((swatch) => {
      // OJO: aquí NO se puede usar `mouseenter`. Una pantalla táctil dispara
      // eventos de mouse fingidos después del toque, en este orden:
      //   toque -> mouseenter -> focus -> click
      // Con `mouseenter` la miniatura quedaba marcada como "la que se ve"
      // antes de que llegara el clic, y entonces el clic la daba por vista y
      // entraba al producto. Por eso al primer toque se iba de largo.
      // `pointerenter` sí distingue de dónde vino, y filtramos solo el mouse.
      if (hasPointerEvents) {
        swatch.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.preview(swatch);
        });
      } else {
        swatch.addEventListener('mouseenter', () => this.preview(swatch));
      }

      // Mismo problema con el foco: al tocar, el navegador enfoca el enlace.
      // :focus-visible es verdadero solo cuando se llegó con el teclado.
      swatch.addEventListener('focus', () => {
        if (swatch.matches && !swatch.matches(':focus-visible')) return;
        this.preview(swatch);
      });

      // Se anota con qué se tocó ANTES del clic, para decidir en el clic.
      // Preguntar por el dispositivo con matchMedia('(hover: hover)') no sirve:
      // una laptop con pantalla táctil responde que sí tiene mouse, aunque el
      // cliente esté usando el dedo.
      swatch.addEventListener('pointerdown', (event) => {
        this.pointerType = event.pointerType;
      });

      // Respaldo para navegadores viejos que no manejan eventos de puntero
      swatch.addEventListener('touchstart', () => { this.pointerType = 'touch'; }, { passive: true });

      swatch.addEventListener('click', (event) => this.onClick(event, swatch));
    });
  }

  onClick(event, swatch) {
    const usedFinger = this.pointerType === 'touch' || this.pointerType === 'pen';

    // Con mouse el hover ya mostró la variante: el clic entra al producto
    if (!usedFinger) return;

    // Si esta miniatura ya es la que se está viendo, el toque sí entra
    if (swatch.classList.contains('is-active')) return;

    event.preventDefault();
    this.preview(swatch);
  }

  preview(swatch) {
    const full = swatch.dataset.full;
    if (!full || this.image.src === full) return;

    // srcset manda sobre src: si no lo quitamos, el navegador sigue mostrando la foto vieja
    this.image.removeAttribute('srcset');
    this.image.removeAttribute('sizes');
    this.image.src = full;

    this.swatches.forEach((s) => s.classList.toggle('is-active', s === swatch));
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
